import { createSelector } from 'reselect';

export const selectStateLandingContent = (state) => state.content.stateLanding;
export const selectBaseUrl = (state) => state.app.baseUrl;

export const organizationSchema = (baseUrl, orgId) => {
	return {
		'@type': 'Organization',
		'@id': orgId,
		name: 'SpareFoot',
		url: baseUrl,
		sameAs: [
			'https://twitter.com/SpareFoot',
			'https://www.facebook.com/SpareFoot',
			'https://www.instagram.com/sparefoot/',
			'https://www.pinterest.com/sparefoot/',
		],
	};
};

export const websiteSchema = (baseUrl, fullState, stateSlug, orgId, siteId) => {
	return {
		'@type': 'WebSite',
		'@id': siteId,
		url: baseUrl + '/',
		name: 'SpareFoot',
		publisher: { '@id': orgId },
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: `${baseUrl}/search?location={search_term_string}&state=${stateSlug}`,
				inLanguage: 'en-US',
				actionPlatform: [
					'https://schema.org/DesktopWebPlatform',
					'https://schema.org/MobileWebPlatform',
				],
			},
			'query-input': {
				'@type': 'PropertyValueSpecification',
				valueRequired: true,
				valueName: 'search_term_string',
				valuePattern: `${fullState}, USA`,
			},
		},
	};
};

export const webPageSchema = (
	baseUrl,
	fullState,
	pageUrl,
	pageId,
	breadcrumbId
) => {
	return {
		'@type': 'WebPage',
		'@id': pageId,
		url: pageUrl,
		inLanguage: 'en',
		mainEntityOfPage: { '@id': pageId },
		name: `Self Storage in ${fullState} | Find the Best Local Storage Units`,
		description: `Find and compare storage units across ${fullState}. Reserve an affordable storage unit in your city with no credit card required.`,
		isPartOf: { '@id': `${baseUrl}#website` },
		publisher: { '@id': `${baseUrl}#organization` },
		about: {
			'@type': 'Place',
			name: fullState,
		},
		breadcrumb: { '@id': breadcrumbId },
	};
};

export const breadcrumbSchema = (baseUrl, pageUrl, fullState, breadcrumbId) => {
	return {
		'@type': 'BreadcrumbList',
		'@id': breadcrumbId,
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: baseUrl,
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: `Self Storage in ${fullState}`,
				item: pageUrl,
			},
		],
	};
};

export const selectStateLandingSchema = createSelector(
	[selectStateLandingContent, selectBaseUrl],
	(content, baseUrl) => {
		const fullState = content?.fullState;
		if (!fullState || !baseUrl) return null;

		const stateName = fullState.toLowerCase();
		const stateSlug = stateName.replace(/\s+/g, '-');
		const pageUrl = `${baseUrl}/${stateSlug}-storage.html`;

		const orgId = `${baseUrl}#organization`;
		const siteId = `${baseUrl}#website`;
		const pageId = `${pageUrl}#webpage`;
		const breadcrumbId = `${pageUrl}#breadcrumb`;

		return {
			'@context': 'https://schema.org',
			'@graph': [
				organizationSchema(baseUrl, orgId),
				websiteSchema(baseUrl, stateName, stateSlug, orgId, siteId),
				webPageSchema(
					baseUrl,
					stateName,
					pageUrl,
					pageId,
					breadcrumbId
				),
				breadcrumbSchema(baseUrl, pageUrl, stateName, breadcrumbId),
			],
		};
	}
);
